import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ReviewScreen.css";

// Icons for better UI
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  RightOutlined,
  LeftOutlined
} from '@ant-design/icons';

const ReviewScreen = (props) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract props if provided
  const {
    wrongQuestions: propsWrongQuestions,
    subject: propsSubject,
    classLevel: propsClassLevel,
    examTitle: propsExamTitle,
    onBack: propsOnBack
  } = props;
  
  // Get data passed from navigation state (React Router)
  const { 
    wrongQuestions: stateWrongQuestions = [], 
    subject: stateSubject = "Unknown Subject", 
    classLevel: stateClassLevel = "Unknown Class",
    examTitle: stateExamTitle = "Exam Review"
  } = location.state || {};
  
  // Priority: props > location.state > defaults
  const wrongQuestions = propsWrongQuestions || stateWrongQuestions || [];
  const subject = propsSubject || stateSubject || "Unknown Subject";
  const classLevel = propsClassLevel || stateClassLevel || "Unknown Class";
  const examTitle = propsExamTitle || stateExamTitle || "Exam Review";
  
  // Determine if we're using React Router
  const isUsingRouter = location.pathname.includes('/review') && !propsOnBack;
  
  // Handle back navigation based on usage context
  const handleBack = () => {
    if (propsOnBack) {
      propsOnBack();
    } else {
      navigate(-1); // Go back in history for React Router
    }
  };

  useEffect(() => {
    // Reset to first question when wrongQuestions changes
    setCurrentQuestionIndex(0);
  }, [wrongQuestions]);

  if (!wrongQuestions || wrongQuestions.length === 0) {
    return (
      <div className="review-container">
        <div className="no-mistakes">
          <h2>🎉 No Mistakes to Review!</h2>
          <p>You answered all questions correctly. Great job!</p>
          <button 
            className="back-btn" 
            onClick={handleBack}
          >
            <ArrowLeftOutlined /> Back to Results
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = wrongQuestions[currentQuestionIndex] || {};
  const progress = ((currentQuestionIndex + 1) / wrongQuestions.length) * 100;

  const handleNext = () => {
    if (currentQuestionIndex < wrongQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  return (
    <div className="review-container">
      {/* Header */}
      <div className="review-header">
        <button 
          className="back-button-header" 
          onClick={handleBack}
        >
          <ArrowLeftOutlined />
          <span>Back to Results</span>
        </button>
        <div className="header-content">
          <h1 className="review-title">Review Mistakes</h1>
          <div className="review-subtitle">
            <span className="subject-badge">{subject}</span>
            <span className="class-badge">{classLevel}</span>
            <span className="exam-title">{examTitle}</span>
          </div>
        </div>
        <div className="header-stats">
          <div className="mistakes-count">
            {wrongQuestions.length} mistake{wrongQuestions.length !== 1 ? 's' : ''} to review
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="review-progress">
        <div className="progress-info">
          <div className="progress-text">
            Question <strong>{currentQuestionIndex + 1}</strong> of <strong>{wrongQuestions.length}</strong>
          </div>
          <div className="progress-percentage">{Math.round(progress)}%</div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }} 
          />
        </div>
      </div>

      {/* Question Card */}
      <div className="question-card">
        {/* Question Number */}
        <div className="question-header">
          <div className="question-number">
            Question #{currentQuestionIndex + 1}
            {currentQuestion.sectionName && (
              <span className="section-name"> • {currentQuestion.sectionName}</span>
            )}
          </div>
        </div>

        {/* Question Text */}
        <h3 className="question-text">{currentQuestion.question || "No question text available"}</h3>
        
        {/* Options */}
        <div className="options-review">
          {currentQuestion.options && currentQuestion.options.map((option, index) => {
            const isCorrect = index === currentQuestion.correctAnswer;
            const isSelected = index === currentQuestion.selectedAnswer || index === currentQuestion.selected;
            
            return (
              <div
                key={index}
                className={`option-review ${isCorrect ? 'correct' : ''} ${
                  isSelected && !isCorrect ? 'incorrect' : ''
                }`}
              >
                <div className="option-header">
                  <span className="option-letter">{String.fromCharCode(65 + index)}.</span>
                  <div className="option-status">
                    {isCorrect && (
                      <span className="status-correct">
                        <CheckOutlined /> Correct Answer
                      </span>
                    )}
                    {isSelected && !isCorrect && (
                      <span className="status-incorrect">
                        <CloseOutlined /> Your Answer
                      </span>
                    )}
                  </div>
                </div>
                <div className="option-text">{option}</div>
              </div>
            );
          })}
        </div>

        {/* Explanation */}
        <div className="explanation-section">
          <h4>
            <span className="explanation-icon">💡</span> Explanation
          </h4>
          <p className="explanation-text">
            {currentQuestion.explanation || "No explanation available for this question."}
          </p>
        </div>

        {/* Topics/Tags */}
        {currentQuestion.topics && currentQuestion.topics.length > 0 && (
          <div className="topics-section">
            <h4>Related Topics</h4>
            <div className="topics-list">
              {currentQuestion.topics.map((topic, index) => (
                <span key={index} className="topic-tag">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="review-navigation">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className="nav-btn prev-btn"
        >
          <LeftOutlined /> Previous
        </button>
        
        <div className="navigation-center">
          <button onClick={handleBack} className="nav-btn back-btn">
            Back to Results
          </button>
          <div className="question-counter">
            {currentQuestionIndex + 1} / {wrongQuestions.length}
          </div>
        </div>
        
        <button
          onClick={handleNext}
          disabled={currentQuestionIndex === wrongQuestions.length - 1}
          className="nav-btn next-btn"
        >
          Next <RightOutlined />
        </button>
      </div>

      {/* Quick Jump */}
      <div className="question-jump">
        <div className="jump-label">Jump to question:</div>
        <div className="jump-buttons">
          {wrongQuestions.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentQuestionIndex(index)}
              className={`jump-btn ${currentQuestionIndex === index ? 'active' : ''}`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewScreen;